import MsgReader from "@kenjiuno/msgreader";
import PostalMime from "postal-mime";
import CryptoJS, { MD5 } from "crypto-js";
import { toMysqlDateTime } from "./dateUtils";
import { EMAIL_FILE_EXTENSIONS } from "./emailFileTypes";

const [EML_EXTENSION, MSG_EXTENSION] = EMAIL_FILE_EXTENSIONS.map((ext) =>
	ext.replace(".", "")
);

window.MD5 = MD5;
window.CryptoJS = CryptoJS;

export async function getEmailData(files, progressCallback) {
	let emailsToUpload = [];
	let failedToParseEmails = [];
	let parsed, spl;

	for (let i = 0; i < files.length; i++) {
		let file = files[i];
		spl = file.name.split(".");
		const emailFileExtensionLowerCase = spl[spl.length - 1].toLowerCase();

		progressCallback &&
			progressCallback({
				counter: i + 1,
				total: files.length,
				filename: file.name,
			});

		if (emailFileExtensionLowerCase === EML_EXTENSION) {
			parsed = await parseEml(file);
			if (parsed.failed) {
				failedToParseEmails.push({ name: file.name });
			} else {
				let nextEmail = handleEmlData(parsed, file);
				emailsToUpload.push(nextEmail);
			}
		} else if (emailFileExtensionLowerCase === MSG_EXTENSION) {
			parsed = await parseMsg(file);
			let nextEmail = handleMsgData(parsed, file);

			emailsToUpload.push(nextEmail);
		}
	}
	return { emailsToUpload, failedToParseEmails };
}

async function parseEml(source) {
	return new Promise((resolve, reject) => {
		let reader = new FileReader();

		reader.onload = function () {
			try {
				let contents = reader.result;

				let checksum = MD5(contents);
				let md5checksum = checksum.toString(CryptoJS.enc.Hex);

				const parser = new PostalMime();

				try {
					parser
						.parse(contents)
						.then((parsed) => {
							resolve({ ...parsed, md5checksum });
						})
						.catch((err) => {
							console.log(
								"postal-mime parse error (promise catch):",
								err
							);
							resolve({
								failed: true,
							});
						});
				} catch (parseErr) {
					console.log(
						"postal-mime parse error (immediate throw):",
						parseErr
					);
					resolve({
						failed: true,
					});
				}
			} catch (err) {
				console.log("postal-mime synchronous error:", err);
				resolve({
					failed: true,
				});
			}
		};
		reader.onerror = function () {
			reader.abort();
		};
		reader.onabort = function () {
			reject("file read aborted");
		};
		reader.readAsText(source);
	});
}

function removeNullCharAtEnd(text = "") {
	// eslint-disable-next-line no-control-regex
	return typeof text === "string" ? text.replace(/\u0000$/, "") : "";
}

function emailHashV2({ sentDateTime, fromEmailAddress, subjectline }) {
	/*
	 
	 Note that this was changed as of ~May 2025 to be consistent with
	 the LexWorkplace for Outlook email hash calculation.

	 Emails uploaded prior to this used a different hash calculation, and thus
	 the de-duplication will not work for the same email uploaded before/after this time

	*/

	// TODO: The Hash should always and only be calculated on the lxw api
	// the frontend should not do this
	const uniqueEmailIdentifier = `${sentDateTime}${fromEmailAddress}${subjectline}`;

	const emailHash = MD5(uniqueEmailIdentifier).toString(CryptoJS.enc.Hex);
	return emailHash;
}

function handleEmlData(parsed, file) {
	const sentDateObj = parsed.date ? new Date(parsed.date) : new Date();
	const sentDateTime = sentDateObj.toISOString().slice(0, 19) + "Z";
	const fromEmailAddress = parsed.from?.address ? parsed.from.address : "";
	const subjectline = parsed.subject ? parsed.subject : "(No Subject)";

	const emailHash = emailHashV2({
		sentDateTime,
		fromEmailAddress,
		subjectline,
	});
	let myEmailData = {
		date: toMysqlDateTime(sentDateObj),
		subjectline: parsed.subject ? parsed.subject : "(No Subject)",
		originalFilename: file.name,
		emailFrom: parsed.from ? postalMimeToString(parsed.from) : "",
		emailTo: parsed.to ? postalMimeToArray(parsed.to) : "",
		filesize: file.size,
		numAttachments: parsed.attachments ? parsed.attachments.length : 0,
		md5checksum: emailHash,
		dateObj: sentDateObj,
	};
	return myEmailData;
}

function postalMimeToString(addressObj) {
	// postal-mime returns { name: "Name", address: "email@example.com" }
	if (addressObj) {
		return addressObj.name || addressObj.address || "";
	}
	return "";
}

function postalMimeToArray(addressArray) {
	// postal-mime returns an array of { name: "Name", address: "email@example.com" }
	if (addressArray && Array.isArray(addressArray)) {
		return addressArray.map((val) => val.name || val.address).join(", ");
	}
	return "";
}

function parseMsg(source) {
	return new Promise((resolve, reject) => {
		var reader = new FileReader();

		reader.onload = function () {
			var contents = reader.result;

			try {
				let parsedMsg = new MsgReader(contents);
				let parsedMsgInfo = parsedMsg.getFileData();
				// MD5 doesn't work with the array buffer from contents
				// headers has a lot of good info for an MD5 hash though
				var checksum = MD5(parsedMsgInfo.headers);
				var md5checksum = checksum.toString(CryptoJS.enc.Hex);
				resolve({ ...parsedMsgInfo, md5checksum });
			} catch (err) {
				reject(err);
			}
		};
		reader.onerror = function () {
			reader.abort();
		};
		reader.onabort = function () {
			reject("file read aborted");
		};
		reader.readAsArrayBuffer(source);
	});
}

function handleMsgData(parsed, file) {
	// Windows New Outlook seems to export .msg files with all these fields having
	// the "null" character \u0000 aka \x00 at the end.  Classic outlook does not
	// do this.  Here we strip these characters out of the fields
	const subjectline = removeNullCharAtEnd(
		parsed.subject ? parsed.subject : "(No Subject)"
	);

	const emailFrom = removeNullCharAtEnd(
		parsed.senderName || parsed.senderSmtpAddress || " "
	);

	let fromEmailAddress = "";

	if (parsed.senderSmtpAddress) {
		fromEmailAddress = parsed.senderSmtpAddress;
	} else if (parsed.creatorSMTPAddress) {
		/*
			when drag & dropping from the "sent items" folder
			in Classic Outlook Windows,
			the senderSmtpAddress seems not to exist, but this
			creatorSMTPAddress does
		*/
		fromEmailAddress = parsed.creatorSMTPAddress;
	}

	const emailTo = parsed.recipients ? msgToMap(parsed.recipients) : "";

	let clientSubmitTime = parsed.clientSubmitTime
		? new Date(parsed.clientSubmitTime)
		: new Date();

	const sentDateTime = clientSubmitTime.toISOString().slice(0, 19) + "Z";

	const emailHash = emailHashV2({
		sentDateTime,
		fromEmailAddress: removeNullCharAtEnd(fromEmailAddress),
		subjectline,
	});

	return {
		date: toMysqlDateTime(clientSubmitTime),
		subjectline,
		originalFilename: file.name,
		emailFrom,
		emailTo,
		filesize: file.size,
		numAttachments: msgGetNumAttached(parsed.attachments),
		md5checksum: emailHash,
		dateObj: clientSubmitTime,
	};
}

function msgToMap(data) {
	if (data && Array.isArray(data)) {
		return data
			.map((val) => removeNullCharAtEnd(val.name || val.smtpAddress))
			.join(", ");
	} else {
		return "";
	}
}

function msgGetNumAttached(attachmentsArray) {
	let notHiddenAttachments = attachmentsArray.filter(
		(a) => !a.attachmentHidden
	);
	return notHiddenAttachments.length;
}
